import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import Signature from 'components/rewards/deliverableItem/DeliverableItemFooter/jersey/Signature';
import decorator from 'components/rewards/deliverableItem/DeliverableItemFooter/jersey/assets/decorator.png';
import jerseyFront from 'components/rewards/deliverableItem/DeliverableItemFooter/jersey/assets/jersey_front.png';
import mysteryLogo from 'components/rewards/deliverableItem/DeliverableItemFooter/jersey/assets/mystery_logo.png';

import { JerseyRewardCard_JerseyDetails } from './__generated__/index.graphql';

const CardWrapper = styled.div`
  position: relative;
`;

const Decorator = styled.img`
  width: 38%;
`;

const ClubAvatarWrapper = styled.div`
  max-width: 17%;
  --size: 100%;

  img {
    max-width: 100%;
  }
`;

const Metas = styled(Vertical).attrs({ center: true, gap: 1 })`
  position: absolute;
  width: 100%;
  bottom: 12px;
  left: 0;

  svg {
    max-width: 90%;
  }

  @media ${mobileAndAbove} {
    gap: var(--double-unit);
    bottom: 20px;
  }
`;

const FirstRow = styled.p`
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 10px;
  font-weight: bold;

  @media ${mobileAndAbove} {
    font-size: 12px;
  }
`;

const SecondRow = styled.p`
  text-transform: uppercase;
  font-size: 16px;
  font-weight: bold;

  @media ${mobileAndAbove} {
    font-size: 22px;
  }
`;

const CardFront = styled.img`
  max-width: 100%;
  width: 100%;
  aspect-ratio: 0.625;
`;

const messages = defineMessages({
  signedJersey: {
    id: 'JerseyRewardCard.signedJersey2',
    defaultMessage: '<firstRow>Signed</firstRow><secondRow>Jersey</secondRow>',
  },
  unsignedJersey: {
    id: 'JerseyRewardCard.unsignedJersey',
    defaultMessage:
      '<firstRow>Unsigned</firstRow><secondRow>Jersey</secondRow>',
  },
});

const JerseyRewardCard = ({
  jersey,
}: {
  jersey: JerseyRewardCard_JerseyDetails;
}) => {
  const { signed, team, competition } = jersey;
  const logoUrl = team?.pictureUrl || competition?.pictureUrl || mysteryLogo;
  return (
    <CardWrapper>
      <CardFront src={jerseyFront} />
      <Metas>
        <div>
          {signed && <Signature />}
          <FormattedMessage
            {...(signed ? messages.signedJersey : messages.unsignedJersey)}
            values={{
              firstRow: (...chunks) => <FirstRow>{chunks}</FirstRow>,
              secondRow: (...chunks) => <SecondRow>{chunks}</SecondRow>,
            }}
          />
        </div>
        <Decorator alt="" src={decorator} />
        <ClubAvatarWrapper>
          <img alt="" src={logoUrl} />
        </ClubAvatarWrapper>
      </Metas>
    </CardWrapper>
  );
};

JerseyRewardCard.fragments = {
  jersey: gql`
    fragment JerseyRewardCard_JerseyDetails on JerseyDeliverableItem {
      slug
      id
      signed
      competition {
        slug
        name
        pictureUrl
      }
      team {
        ... on TeamInterface {
          slug
          name
          pictureUrl
        }
      }
    }
  ` as TypedDocumentNode<JerseyRewardCard_JerseyDetails>,
};

export default JerseyRewardCard;
